import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Copy, GitMerge } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Aug 2026 · Prod incident (QP-6211): duplicate Viatris workbench rows made
// counts flicker and bulk actions land on the hidden twin. This tool lists
// each duplicate pair and merges them without losing any status.
export default function ViatrisDuplicatesPage() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [merging, setMerging] = useState(null); // asset_id | 'all'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/viatris/duplicates`, { withCredentials: true });
      setItems(r.data.items || []);
    } catch { toast.error('Failed to load duplicates'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const merge = async (assetId) => {
    setMerging(assetId || 'all');
    try {
      const r = await axios.post(`${API}/viatris/duplicates/merge`,
        assetId ? { asset_id: assetId } : { all: true }, { withCredentials: true });
      toast.success(`Merged ${r.data.merged_groups} group(s), removed ${r.data.removed_rows} duplicate row(s). Approved / Team Approved / Responded kept.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Merge failed'); }
    finally { setMerging(null); }
  };

  const flag = (v) => v ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[9px]">Yes</Badge> : <span className="text-[10px] text-slate-300">—</span>;

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-6" data-testid="viatris-duplicates-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Copy className="w-5 h-5 inline mr-2 text-violet-600" />Viatris Duplicate Rows
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Assets with more than one workbench row. Duplicates make shortlist counts flicker and let
            bulk actions (Responded, Team Approved) land on a hidden twin. Merging keeps the best of
            every field — Approved, Team Approved, Responded and notes are never lost. Removed rows
            are backed up before deletion.
          </p>
        </div>
        {items.length > 0 && (
          <Button onClick={() => merge(null)} disabled={!!merging} className="bg-violet-700 hover:bg-violet-800 text-white" data-testid="merge-all-btn">
            {merging === 'all' ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Merging…</> : <><GitMerge className="w-4 h-4 mr-1.5" /> Merge all ({items.length})</>}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>
      ) : items.length === 0 ? (
        <Card className="border-emerald-200 bg-emerald-50/40"><CardContent className="py-8 text-center text-sm text-emerald-700" data-testid="no-duplicates">
          No duplicate rows — every Viatris asset has exactly one workbench row.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {items.map(g => (
            <Card key={g.asset_id} className="border-slate-100" data-testid={`dup-group-${g.asset_id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div>
                    <span className="text-sm font-semibold text-slate-900">{g.asset_name || '(unnamed asset)'}</span>
                    <span className="text-xs text-slate-400 ml-2">{g.biotech_company_name}</span>
                    <Badge variant="secondary" className="ml-2 text-[9px] bg-rose-50 text-rose-600">{g.rows.length} rows</Badge>
                  </div>
                  <Button size="sm" variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50"
                    onClick={() => merge(g.asset_id)} disabled={!!merging} data-testid={`merge-btn-${g.asset_id}`}>
                    {merging === g.asset_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Merge'}
                  </Button>
                </div>
                <div className="grid grid-cols-[1fr_90px_80px_80px_80px_110px] gap-2 text-[10px] uppercase tracking-wide text-slate-400 font-semibold px-2">
                  <span>Row</span><span>Committee</span><span>Team Appr.</span><span>Responded</span><span>Cohort</span><span>Updated</span>
                </div>
                {g.rows.map(r => (
                  <div key={r.id} className="grid grid-cols-[1fr_90px_80px_80px_80px_110px] gap-2 items-center px-2 py-1.5 border-t border-slate-50 text-xs text-slate-600">
                    <span className="font-mono text-[10px] text-slate-400">{r.id.slice(0, 8)}…</span>
                    <span>{r.committee_status || '—'}</span>
                    <span>{flag(r.team_approved)}</span>
                    <span>{flag(r.responded)}</span>
                    <span>{r.reporting_cohort_name || (r.phase_id ? `Phase ${r.phase_id}` : 'Phase 1')}</span>
                    <span className="text-[10px] text-slate-400">{(r.updated_at || r.created_at || '').slice(0, 10)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
